import AddIcon from '@mui/icons-material/Add'
import {
  Button,
  createFilterOptions,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
} from '@mui/material'
import { get, isEmpty } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import uuidv4 from 'uuid/v4'
import { useActions } from '../hooks/useActions'
import { useLocalization } from '../redux/reduxHooks'
import * as CancerTypes from '../utils/CancerTypes'
import { getLabel, pathToPerson, renderContent } from '../utils/helpers'
import { PersonFieldPaths } from '../utils/QuestionnaireStateManager.js'
import AddNewModal from './AddNewModal'
import CancerList from './CancerList'
import ConditionalSection from './ConditionalSection.jsx'
import IntegerDetails from './IntegerDetails.jsx'
import SearchSelect from './SearchSelect'
import TextFieldQuestion from './TextFieldQuestion.jsx'

const filter = createFilterOptions()
const cancerCategoryMap = [
  { type: 'BREAST_TYPES', label: 'Breast' },
  { type: 'OVARIAN_TYPES', label: 'Ovarian' },
  { type: 'SKIN_TYPES', label: 'Skin' },
  { type: 'KIDNEY_AND_URINARY_TYPES', label: 'Kidney and urinary tract' },
  { type: 'BRAIN_TYPES', label: 'Brain' },
  { type: 'COLON_TYPES', label: 'Colon (Bowel)' },
]

export default function CancerInput({ personId, config }) {
  const [modalOpened, setModalOpened] = useState(false)
  // the input from the search field, some cancers need the type to be specified
  const [cancerCategory, setCancerCategory] = useState({})
  // same as category if no type is needed, otherwise the 2nd drop down field
  const [cancer, setCancer] = useState({})

  const localization = useLocalization()
  const sex = useSelector((state) => get(state, [...pathToPerson(personId), 'sex'])) || ''
  const probandId = useSelector((state) => get(state, ['questionnaire', 'probandId'])) || ''
  const { addPersonCancer } = useActions()

  const cancerListFiltered = CancerTypes.CANCER_TYPES.filter((cancerType) => {
    // check for gender specific cancers
    if (!cancerType.genderRestriction || cancerType.genderRestriction === sex) {
      return cancerType
    } else {
      return null
    }
  })

  return (
    <>
      <FormLabel>{getLabel(localization, PersonFieldPaths.TYPES_OF_CANCER, 'person')}</FormLabel>
      <AddNewModal
        open={modalOpened}
        handleClose={handleClose}
        dialogTitle="Add Cancer"
        onSubmit={addCancer}
        disabled={!('id' in cancer)}
        cancelButton
      >
        <SearchSelect
          label="Search Cancers"
          value={{ label: cancerCategory.label || '', value: cancerCategory.id || '' }}
          options={cancerToOption(cancerListFiltered)}
          onChange={(_, newValue) => {
            // search field is reset (x button)
            if (!newValue) {
              resetValues()

              return
            }
            const newCancer = cancerListFiltered.find(({ id }) => id === newValue.value)

            // same cancer was selected
            if (newValue.value === cancerCategory.id) return
            // set category
            resetValues()
            setCancerCategory(newCancer)
            // other option, custom cancer field
            if (newCancer.id === 'Other') {
              setCancerCategory({ id: 'other', label: 'Other' })

              return
            }
            // set cancer = cancerCategory if there is no specific cancer type
            if (!getCancerTypes(newCancer.label)) {
              setCancer(newCancer)
            }
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params)
            const otherOption = {
              label: 'Other',
              value: 'Other',
            }

            // always keep other option in view
            if (!filtered.find((item) => item.id === otherOption.id)) {
              filtered.push(otherOption)
            }

            return filtered
          }}
        />
        <CancerDetails
          cancer={cancer}
          setCancer={setCancer}
          isProband={probandId === personId}
          config={config}
          getCancerTypes={getCancerTypes}
          cancerCategory={cancerCategory}
        />
      </AddNewModal>
      <CancerList personId={personId} editCancer={editCancer} />
      <Button startIcon={<AddIcon />} onClick={() => setModalOpened(true)}>
        Add Diagnosis
      </Button>
    </>
  )

  function cancerToOption(cancers) {
    return cancers.map(({ id, label }) => ({
      value: id,
      label,
    }))
  }
  function handleClose() {
    setModalOpened(false)
    resetValues()
  }
  function addCancer() {
    addPersonCancer({ id: personId, cancer })
    handleClose()
  }
  function editCancer(_cancer) {
    if (_cancer.id === 'other') {
      setCancerCategory({ id: 'other', label: 'Other' })
    } else {
      let _cancerCategory
      for (const { type, label } of cancerCategoryMap) {
        if (CancerTypes[type].some((c) => c.id === _cancer.id)) {
          _cancerCategory = CancerTypes.CANCER_TYPES.find((c2) => c2.label === label)
          break
        }
      }
      setCancerCategory(_cancerCategory || _cancer)
    }
    setCancer(_cancer)
    setModalOpened(true)
  }
  function resetValues() {
    setCancerCategory({})
    setCancer({})
  }
  function getCancerTypes(_label) {
    for (const { type, label } of cancerCategoryMap) {
      if (label === _label) {
        return CancerTypes[type]
      }
    }

    return null
  }
}

function CancerDetails({ cancerCategory, cancer, setCancer, isProband, config, getCancerTypes }) {
  const [treatment, setTreatment] = useState(cancer.treatments?.[0] ?? {})
  const [qualifier, setQualifier] = useState(cancer.qualifiers?.[0] ?? {})
  const [qualifier2, setQualifier2] = useState(cancer.qualifiers?.[1] ?? {})

  useEffect(() => {
    const _cancer = { ...cancer }
    if (!isEmpty(treatment)) {
      _cancer.treatments = [treatment]
    }
    if (!isEmpty(qualifier)) {
      _cancer.qualifiers = [qualifier]
    }
    if (!isEmpty(qualifier2)) {
      _cancer.qualifiers.push(qualifier2)
    }
    setCancer({ ...cancer, ..._cancer })
  }, [treatment, qualifier, qualifier2])

  return (
    <>
      {cancerCategory.id === 'other' ? (
        <CancerOtherType cancer={cancer} setCancer={setCancer} />
      ) : (
        <CancerType
          resetCancerDetails={resetCancerDetails}
          cancer={cancer}
          setCancer={setCancer}
          cancerCategory={cancerCategory}
          getCancerTypes={getCancerTypes}
        />
      )}
      {cancer.id
        ? renderContent({
            renderMap: {
              cancerDiagnoseAge: (
                <AgeAtDiagnosis
                  key="cancerAge"
                  qualifier={qualifier}
                  addQualifierProperty={addQualifierProperty}
                  index={1}
                />
              ),
              cancerHospitalLocation: (
                <TreatmentLocation
                  key="cancerHospital"
                  treatment={treatment}
                  addTreatmentProperty={addTreatmentProperty}
                />
              ),
              cancerTreatmentAddress: (
                <PatientAddress
                  key="cancerTreatmentAddress"
                  treatment={treatment}
                  addTreatmentProperty={addTreatmentProperty}
                />
              ),
              cancerSide: (
                <BreastCancerQuestions
                  key="breastCancerQuestions"
                  qualifier={qualifier}
                  qualifier2={qualifier2}
                  addQualifierProperty={addQualifierProperty}
                  cancer={cancer}
                  cancerCategory={cancerCategory}
                  isProband={isProband}
                  setQualifier2={setQualifier2}
                />
              ),
            },
            config,
          })
        : null}
    </>
  )

  function resetCancerDetails() {
    setTreatment({})
    setQualifier({})
    setQualifier2({})
  }
  function addTreatmentProperty(item) {
    if (isEmpty(treatment)) {
      setTreatment({ type: 'other', ...item })
    } else {
      setTreatment({ ...treatment, ...item })
    }
  }
  function addQualifierProperty(item, index) {
    const _setQualifier = index === 2 ? setQualifier2 : setQualifier
    const _qualifier = index === 2 ? qualifier2 : qualifier

    if (isEmpty(_qualifier)) {
      _setQualifier({ id: uuidv4(), ...item })
    } else {
      _setQualifier({ ..._qualifier, ...item })
    }
  }
}

function BreastCancerQuestions({
  qualifier2,
  qualifier,
  addQualifierProperty,
  setQualifier2,
  cancer,
  cancerCategory,
  isProband,
}) {
  const [secondBreastCancer, setSecondBreastCancer] = useState(
    !isEmpty(qualifier2) ? 'Y' : undefined,
  )
  if (cancerCategory.label !== 'Breast') return null

  return (
    <>
      <BreastSideSelect
        qualifier={qualifier}
        addQualifierProperty={addQualifierProperty}
        index={1}
      />
      {cancer.label === 'Breast cancer' ? (
        <ConditionalSection
          key="cancerSecondOccurrence"
          label={
            isProband
              ? 'Have you had breast cancer more than once?'
              : 'Has this person had breast cancer more than once?'
          }
          showWhenCondition="Y"
          conditionState={secondBreastCancer}
          onConditionStateChange={(value) => {
            setSecondBreastCancer(value)
          }}
          orderSchema={['Y', 'N']}
          cleanUpChildQuestions={() => {
            setQualifier2({})
          }}
        >
          <BreastSideSelect
            qualifier={qualifier2}
            addQualifierProperty={addQualifierProperty}
            index={2}
          />
          <AgeAtDiagnosis
            qualifier={qualifier2}
            addQualifierProperty={addQualifierProperty}
            index={2}
          />
        </ConditionalSection>
      ) : null}
    </>
  )
}

function CancerType({ resetCancerDetails, cancerCategory, cancer, setCancer, getCancerTypes }) {
  const cancerTypes = getCancerTypes(cancerCategory.label)
  if (!cancerTypes) {
    return null
  }

  return (
    <FormControl key="cancerType" fullWidth>
      <FormLabel>Select Type</FormLabel>
      <Select
        value={cancer?.id ?? ''}
        onChange={({ target: { value: newCancerId } }) => {
          const newCancer = cancerTypes.find(({ id }) => id === newCancerId)

          if (newCancerId !== cancer?.id) {
            setCancer(newCancer)
            resetCancerDetails()
          }
        }}
      >
        {cancerTypes.map(({ id, label }) => (
          <MenuItem key={id} value={id}>
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

function CancerOtherType({ cancer, setCancer }) {
  return (
    <FormControl key="cancerOtherType">
      <FormLabel>Please Describe</FormLabel>
      <TextFieldQuestion
        fullWidth
        handleChange={(value) => {
          setCancer({ id: 'other', label: value })
        }}
        value={cancer?.label ?? ''}
      />
    </FormControl>
  )
}

function AgeAtDiagnosis({ qualifier, addQualifierProperty, index }) {
  return (
    <FormControl key="cancerAge">
      <FormLabel>Age at diagnosis?</FormLabel>
      <IntegerDetails
        value={qualifier?.numericAgeAtDiagnosis ?? ''}
        onChange={(value) => {
          addQualifierProperty({ numericAgeAtDiagnosis: value }, index)
        }}
      />
    </FormControl>
  )
}

function TreatmentLocation({ treatment, addTreatmentProperty }) {
  return (
    <FormControl key="cancerHospital">
      <FormLabel>Treating hospital or location</FormLabel>
      <TextFieldQuestion
        fullWidth
        handleChange={(value) => {
          addTreatmentProperty({ treatmentLocation: value })
        }}
        value={treatment.treatmentLocation ?? ''}
      />
    </FormControl>
  )
}

function PatientAddress({ treatment, addTreatmentProperty }) {
  return (
    <FormControl key="cancerAddress">
      <FormLabel>Address when treated? (enter town/city if unknown)</FormLabel>
      <TextFieldQuestion
        fullWidth
        handleChange={(value) => {
          addTreatmentProperty({ patientAddress: value })
        }}
        value={treatment.patientAddress ?? ''}
      />
    </FormControl>
  )
}

function BreastSideSelect({ qualifier, addQualifierProperty, index }) {
  return (
    <FormControl key="cancerSide">
      <FormLabel>Which side of the body?</FormLabel>
      <Select
        value={qualifier.laterality ?? ''}
        onChange={({ target: { value } }) => {
          addQualifierProperty({ laterality: value }, index)
        }}
      >
        {CancerTypes.BREAST_LATERALITY.map((i) => (
          <MenuItem key={i.id} value={i.id}>
            {i.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
